import _ from 'lodash';

import { bindServices } from '@/services/api';
import { toPaginate, getFiltersGlobalFilters } from '@/utils/infrastructure';
import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';

const api = bindServices('reportGroups');
const { BulkActionType } = CoreEnums;
const namespace = 'reportGroups';
const fetchRoute = 'reportGroup';
const fetchParams = 'ReportGroup';

const initialPagination = { page: 1, count: 0, totalPages: 0 };

const initialState = {
  list: [],
  pagination: initialPagination,
  orderBy: '',
  select: CoreQueries[fetchParams]?.paged?.select || '',
  searchSelect: CoreQueries[fetchParams]?.paged?.searchSelect || [],
  showForm: false,
  showPermissionForm: false,
  detail: false,
  id: null,
  item: null,
  success: false,
  validationErrors: [],
  userListPermission: null,
  positionListPermission: null,
  userGroupListPermission: null,
  areaListPermission: null,
  permissionsCache: {},
};

const Model = {
  namespace,
  state: initialState,

  effects: {
    *fetchList({ payload }, { call, put, select }) {
      try {
        const pageSize = yield select((state) => state.global.pageSize);
        const params = {
          ...CoreQueries[fetchParams]?.paged,
          pageSize,
          ...payload,
        };
        const res = yield call(api.getAll, {
          route: `${fetchRoute}/paged`,
          params,
          readPriority: true,
        });
        yield put({
          type: 'queryData',
          payload: { ...res, limit: payload.limit ?? pageSize, page: payload.page ?? 1 },
        });
      } catch (e) {
        /*  */
      }
    },

    *fetchItem({ payload }, { call, put }) {
      try {
        yield put({ type: 'success', payload: false });
        const res = yield call(api.getById, {
          route: fetchRoute,
          id: payload.id,
          params: payload.detail
            ? CoreQueries[fetchParams]?.itemDetail
            : { ...CoreQueries[fetchParams]?.item, ...(payload?.params && { ...payload.params }) },
        });
        yield put({ type: 'queryItem', payload: res });
      } catch (e) {
        /*  */
      }
    },

    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = payload.data || payload;
        const cleanData = { ...data };
        cleanData.powerBIServerConfigurationId =
          cleanData.powerBIServerConfigurationId?.value ??
          cleanData.powerBIServerConfigurationId?.id ??
          cleanData.powerBIServerConfigurationId;
        if (_.isObject(cleanData.permissionsFiltersLogicalOperator)) {
          cleanData.permissionsFiltersLogicalOperator =
            cleanData.permissionsFiltersLogicalOperator.value ??
            cleanData.permissionsFiltersLogicalOperator;
        }
        yield call(api.create, { route: fetchRoute, data: cleanData });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchUpdate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const data = payload.data || payload;
        const cleanData = { ...data };
        cleanData.powerBIServerConfigurationId =
          cleanData.powerBIServerConfigurationId?.value ??
          cleanData.powerBIServerConfigurationId?.id ??
          cleanData.powerBIServerConfigurationId;
        if (_.isObject(cleanData.permissionsFiltersLogicalOperator)) {
          cleanData.permissionsFiltersLogicalOperator =
            cleanData.permissionsFiltersLogicalOperator.value ??
            cleanData.permissionsFiltersLogicalOperator;
        }
        yield call(api.update, { route: fetchRoute, data: cleanData });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },

    *fetchDelete({ payload }, { call, put }) {
      try {
        const { id, view } = payload;
        yield put({ type: 'global/modalConfirm', payload: { state: false, id: null } });
        if (view) {
          yield put({
            type: 'queryShowForm',
            payload: { state: false, item: null, detail: false, id: null },
          });
        }
        yield call(api.remove, { route: fetchRoute, id });
        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *fetchActivate({ payload }, { put, call }) {
      try {
        yield call(api.activate, {
          route: fetchRoute,
          id: payload.id,
          isActive: payload.state,
        });
        yield put({ type: 'itemActivate', payload });
      } catch (e) {
        /*  */
      }
    },

    *bulkDeleteReportGroups({ payload }, { call, put, select }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Delete,
          ids,
        });

        const pageSize = yield select((state) => state.global.pageSize);
        const stateData = yield select((state) => state[namespace]);
        const globalState = yield select((state) => state.global);
        const filters = getFiltersGlobalFilters(globalState.globalFilters, namespace);
        const params = {
          ...CoreQueries[fetchParams]?.paged,
          page: 1,
          limit: pageSize,
          ...(globalState.searchCriteria && { search: globalState.searchCriteria }),
          ...(stateData.orderBy && { orderBy: stateData.orderBy }),
          ...(filters && { filter: filters }),
          select: stateData.select,
        };

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'fetchList', payload: params });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkActivateReportGroups({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Activate,
          ids,
        });
        yield put({ type: 'bulkReportGroupActivate', payload: { ids, isActive: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *bulkDeactivateReportGroups({ payload }, { call, put }) {
      try {
        const { ids } = payload;
        if (!ids || ids.length === 0) return;

        yield call(api.bulkAction, {
          route: fetchRoute,
          bulkActionType: BulkActionType.Deactivate,
          ids,
        });
        yield put({ type: 'bulkReportGroupActivate', payload: { ids, isActive: false } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *showForm({ payload }, { put }) {
      yield put({ type: 'queryShowForm', payload });
      if (!_.isNil(payload.id) && payload.item) {
        yield put({ type: 'editOrDetail', payload });
      }
    },

    *showPermissionForm({ payload }, { put }) {
      yield put({ type: 'queryShowPermissionForm', payload });
    },

    *fetchListPermissions({ payload }, { call, put, select }) {
      try {
        const { route, params, name, id } = payload;
        const currentState = yield select((state) => state[namespace]);
        const fieldMap = {
          userListPermission: 'user',
          positionListPermission: 'position',
          userGroupListPermission: 'userGroup',
          areaListPermission: 'area',
        };
        const field = fieldMap[name] || name;
        const cacheKey = `${id}_${field}`;

        if (currentState[name] !== null && currentState[name] !== undefined) return;

        if (currentState.permissionsCache?.[cacheKey] !== undefined) {
          yield put({
            type: 'queryListPermissions',
            payload: { [name]: currentState.permissionsCache[cacheKey] },
          });
          return;
        }

        const data = yield call(api.getAll, { route, params });
        const processedData = (data || []).map((item) => {
          const subItem = item[`${field}Id`] !== undefined ? item[field] : item;
          const itemId = item[`${field}Id`] || item.id || subItem?.id;
          return {
            ...subItem,
            id: itemId,
            name:
              subItem?.name ?? subItem?.fullName ?? subItem?.displayName ?? subItem?.description,
            fullName: subItem?.fullName ?? subItem?.name,
          };
        });

        yield put({
          type: 'cachePermissionsData',
          payload: { key: cacheKey, data: processedData },
        });
        yield put({
          type: 'queryListPermissions',
          payload: { [name]: processedData },
        });
      } catch (e) {
        /*  */
      }
    },

    *savePermission({ payload }, { call, put, select }) {
      try {
        const { id } = payload;
        let currentState = yield select((state) => state[namespace]);
        const permissionsCache = currentState.permissionsCache || {};

        const permissionConfigs = [
          {
            name: 'userListPermission',
            type: 'user',
            route: `reportGroup/allowedUsers/${id}`,
            params: {
              select: 'id,fullName',
              expand: { position: { select: 'name' } },
              orderBy: 'fullName',
            },
          },
          {
            name: 'positionListPermission',
            type: 'position',
            route: `reportGroup/allowedPositions/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'areaListPermission',
            type: 'area',
            route: `reportGroup/allowedAreas/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
          {
            name: 'userGroupListPermission',
            type: 'userGroup',
            route: `reportGroup/allowedUserGroups/${id}`,
            params: { select: 'id,name,description', orderBy: 'name' },
          },
        ];

        const tabsToLoad = permissionConfigs.filter(
          (config) => permissionsCache[`${id}_${config.type}`] === undefined,
        );

        if (tabsToLoad.length > 0) {
          for (const config of tabsToLoad) {
            const cacheKey = `${id}_${config.type}`;
            const data = yield call(api.getAll, {
              route: config.route,
              params: config.params,
            });
            const processedData = (data || []).map((item) => {
              const field = config.type;
              const subItem = item[`${field}Id`] !== undefined ? item[field] : item;
              const itemId = item[`${field}Id`] || item.id || subItem?.id;
              return {
                ...subItem,
                id: itemId,
                name:
                  subItem?.name ??
                  subItem?.fullName ??
                  subItem?.displayName ??
                  subItem?.description,
                fullName: subItem?.fullName ?? subItem?.name,
              };
            });

            yield put({
              type: 'cachePermissionsData',
              payload: { key: cacheKey, data: processedData },
            });

            currentState = yield select((state) => state[namespace]);
            const currentLocalState = currentState[config.name];
            if (
              currentLocalState === null ||
              (Array.isArray(currentLocalState) && currentLocalState.length === 0)
            ) {
              yield put({
                type: 'queryListPermissions',
                payload: { [config.name]: processedData },
              });
            }
          }
        }

        currentState = yield select((state) => state[namespace]);
        const saveData = {
          users: (currentState.userListPermission || []).map((l) => ({ id: l.id })),
          positions: (currentState.positionListPermission || []).map((l) => ({ id: l.id })),
          userGroups: (currentState.userGroupListPermission || []).map((l) => ({ id: l.id })),
          areas: (currentState.areaListPermission || []).map((l) => ({ id: l.id })),
        };

        yield call(api.apiRoute, {
          route: `${fetchRoute}/updatePermissions/${id}`,
          method: 'post',
          data: saveData,
        });

        const permissionLists = {
          userListPermission: 'user',
          positionListPermission: 'position',
          areaListPermission: 'area',
          userGroupListPermission: 'userGroup',
        };

        currentState = yield select((state) => state[namespace]);
        for (const listName of Object.keys(permissionLists)) {
          const permissionType = permissionLists[listName];
          const cacheKey = `${id}_${permissionType}`;
          const currentList = currentState[listName] || [];
          yield put({
            type: 'cachePermissionsData',
            payload: { key: cacheKey, data: currentList },
          });
        }

        yield put({ type: 'success', payload: { success: true } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (error) {
        if (error?.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: { visible: true, type: 'error', message: error.message },
          });
        }
      }
    },

    *addListPermission({ payload }, { put }) {
      yield put({ type: 'queryAddListPermission', payload });
    },

    *hidePermission(_, { put }) {
      yield put({ type: 'queryHidePermission' });
    },

    *deleteItemPermision({ payload }, { put }) {
      yield put({ type: 'queryDeleteItemPermision', payload });
    },

    *setOrderBy({ payload }, { put }) {
      yield put({ type: 'queryOrderBy', payload });
    },

    *changeSuccess({ payload }, { put }) {
      yield put({ type: 'success', payload });
    },

    *resetState(_, { put }) {
      yield put({ type: 'reset' });
    },
  },

  reducers: {
    queryData(state, { payload }) {
      return {
        ...state,
        list: payload.items,
        pagination: _.merge(state.pagination, toPaginate(payload)),
      };
    },

    itemActivate(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, isActive: payload.state } : item,
      );
      return { ...state, list };
    },

    bulkReportGroupActivate(state, { payload }) {
      const { ids, isActive } = payload;
      const idSet = new Set(ids);
      const list = state.list.map((item) => (idSet.has(item.id) ? { ...item, isActive } : item));
      return { ...state, list };
    },

    queryItem(state, { payload }) {
      return { ...state, item: payload };
    },

    queryShowForm(state, { payload }) {
      return {
        ...state,
        showForm: payload.state,
        detail: payload.detail,
        id: payload.id,
        item: payload.item ?? null,
        success: false,
        validationErrors: [],
      };
    },

    queryShowPermissionForm(state, { payload }) {
      return {
        ...state,
        showPermissionForm: payload.state,
        id: payload.id,
      };
    },

    editOrDetail(state, { payload }) {
      const item = payload.item ?? {};
      const permissionsFiltersLogicalOperator = item.permissionsFiltersLogicalOperator ?? 0;
      return {
        ...state,
        item: { ...item, permissionsFiltersLogicalOperator },
      };
    },

    queryListPermissions(state, { payload }) {
      return {
        ...state,
        userListPermission: payload.userListPermission ?? state.userListPermission,
        positionListPermission: payload.positionListPermission ?? state.positionListPermission,
        userGroupListPermission: payload.userGroupListPermission ?? state.userGroupListPermission,
        areaListPermission: payload.areaListPermission ?? state.areaListPermission,
      };
    },

    queryAddListPermission(state, { payload }) {
      return {
        ...state,
        [payload.name]: [...(state[payload.name] || []), payload.data],
      };
    },

    queryDeleteItemPermision(state, { payload }) {
      return {
        ...state,
        [payload.name]: _.filter(state[payload.name] || [], (l) => l.id !== payload.id),
      };
    },

    queryHidePermission(state) {
      return {
        ...state,
        showPermissionForm: false,
        id: null,
        userListPermission: null,
        positionListPermission: null,
        userGroupListPermission: null,
        areaListPermission: null,
        permissionsCache: {},
      };
    },

    cachePermissionsData(state, { payload }) {
      const { key, data } = payload;
      return {
        ...state,
        permissionsCache: {
          ...state.permissionsCache,
          [key]: data,
        },
      };
    },

    queryOrderBy(state, { payload }) {
      return { ...state, orderBy: payload };
    },

    success(state, { payload }) {
      return {
        ...state,
        ...payload,
        success: payload?.success ?? payload,
        validationErrors: [],
      };
    },

    errors(state, { payload }) {
      return {
        ...state,
        validationErrors: payload.validationErrors || [],
        message: payload.message,
      };
    },

    reset() {
      return { ...initialState };
    },
  },
};

export default Model;
